import { useState, useEffect, useCallback } from 'react';
import { budgetAPI } from '../api/client';
import { formatAmount } from '../utils/currency';
import { Plus, Trash2, Edit2, Bell, X, Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { CATEGORIES as ALL_CATEGORIES } from '../constants/categories';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTokens } from '../context/ThemeContext';

const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'yearly',  label: 'Yearly' },
  { value: 'custom',  label: 'Custom dates' },
];

// ── Utility ──────────────────────────────────────────────────────────────────

function pctColor(pct) {
  if (pct >= 100) return '#ef4444';
  if (pct >= 80)  return '#f59e0b';
  return '#10b981';
}

function ProgressBar({ pct }) {
  const t = useTokens();
  const color = pctColor(pct);
  return (
    <div style={{ height: '8px', backgroundColor: t.chipBg, borderRadius: '4px', overflow: 'hidden' }}>
      <div style={{ height: '100%', borderRadius: '4px', transition: 'width 0.3s ease', width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
    </div>
  );
}

// ── Budget Form (create / edit) ──────────────────────────────────────────────

const BLANK_FORM = {
  name:       '',
  categories: [],
  amount:     '',
  period:     'monthly',
  startDate:  '',
  endDate:    '',
  alerts:     [{ percentage: 80 }],
};

function BudgetForm({ initial, onSave, onCancel, baseCurrency }) {
  const t = useTokens();
  const styles = makeStyles(t);
  const [form, setForm]     = useState(initial || BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleCategory = (cat) => {
    set('categories', form.categories.includes(cat)
      ? form.categories.filter(c => c !== cat)
      : [...form.categories, cat]);
  };

  const addAlert = () => {
    if (form.alerts.length >= 10) return;
    set('alerts', [...form.alerts, { percentage: '' }]);
  };

  const removeAlert = (i) => set('alerts', form.alerts.filter((_, idx) => idx !== i));

  const setAlertPct = (i, val) => {
    const updated = [...form.alerts];
    updated[i] = { ...updated[i], percentage: val };
    set('alerts', updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim())           return setError('Budget name is required.');
    if (!form.categories.length)     return setError('Select at least one category.');
    if (!form.amount || Number(form.amount) <= 0) return setError('Enter a valid budget amount.');
    if (form.period === 'custom' && (!form.startDate || !form.endDate))
      return setError('Start and end dates are required for custom period.');

    const validAlerts = form.alerts
      .map(a => ({ percentage: Number(a.percentage) }))
      .filter(a => a.percentage > 0 && a.percentage <= 100);

    setSaving(true);
    try {
      await onSave({
        name:       form.name.trim(),
        categories: form.categories,
        amount:     Number(form.amount),
        period:     form.period,
        startDate:  form.period === 'custom' ? form.startDate : undefined,
        endDate:    form.period === 'custom' ? form.endDate   : undefined,
        alerts:     validAlerts,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save budget.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.formCard}>
      <div style={styles.formHeader}>
        <h3 style={styles.formTitle}>{initial ? 'Edit Budget' : 'New Budget'}</h3>
        <button type="button" onClick={onCancel} style={styles.iconBtn}>
          <X size={18} color="#6b7280" />
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Name */}
      <label style={styles.label}>Budget Name</label>
      <input
        style={styles.input}
        placeholder="e.g. Monthly Groceries"
        value={form.name}
        onChange={e => set('name', e.target.value)}
        required
      />

      {/* Amount */}
      <label style={styles.label}>Budget Amount ({baseCurrency})</label>
      <input
        style={styles.input}
        type="number"
        min="1"
        step="any"
        placeholder="0.00"
        value={form.amount}
        onChange={e => set('amount', e.target.value)}
        required
      />

      {/* Period */}
      <label style={styles.label}>Period</label>
      <select
        style={styles.select}
        value={form.period}
        onChange={e => set('period', e.target.value)}
      >
        {PERIOD_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {form.period === 'custom' && (
        <div style={styles.dateRow}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Start Date</label>
            <input type="date" style={styles.input} value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>End Date</label>
            <input type="date" style={styles.input} value={form.endDate} onChange={e => set('endDate', e.target.value)} />
          </div>
        </div>
      )}

      {/* Categories */}
      <label style={styles.label}>Categories</label>
      <div style={styles.categoryGrid}>
        {ALL_CATEGORIES.map(cat => {
          const selected = form.categories.includes(cat);
          return (
            <button
              key={cat}
              type="button"
              onClick={() => toggleCategory(cat)}
              style={{ ...styles.catChip, ...(selected ? styles.catChipSelected : {}) }}
            >
              {selected && <Check size={11} />}
              {cat}
            </button>
          );
        })}
      </div>

      {/* Alerts */}
      <div style={styles.alertsHeader}>
        <label style={styles.label}>Alert Thresholds (%)</label>
        <button type="button" onClick={addAlert} style={styles.addAlertBtn}>
          <Plus size={13} /> Add
        </button>
      </div>
      <p style={styles.alertHint}>An email is sent to your registered address when spend crosses each threshold.</p>

      {form.alerts.length === 0 && (
        <p style={{ ...styles.alertHint, color: '#9ca3af', fontStyle: 'italic' }}>No alerts set — add one above.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {form.alerts.map((a, i) => (
          <div key={i} style={styles.alertRow}>
            <input
              type="number"
              min="1"
              max="100"
              style={{ ...styles.input, flex: 1, marginBottom: 0 }}
              placeholder="e.g. 80"
              value={a.percentage}
              onChange={e => setAlertPct(i, e.target.value)}
            />
            <span style={styles.alertPctLabel}>%</span>
            <button type="button" onClick={() => removeAlert(i)} style={styles.iconBtn}>
              <X size={14} color="#ef4444" />
            </button>
          </div>
        ))}
      </div>

      <div style={styles.formActions}>
        <button type="button" onClick={onCancel} style={styles.cancelBtn}>Cancel</button>
        <button type="submit" disabled={saving} style={styles.saveBtn}>
          {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
          {saving ? 'Saving…' : 'Save Budget'}
        </button>
      </div>
    </form>
  );
}

// ── Budget Card ───────────────────────────────────────────────────────────────

function BudgetCard({ budget, baseCurrency, onEdit, onDelete }) {
  const t = useTokens();
  const styles = makeStyles(t);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { name, categories, amount, period, spent = 0, remaining = 0, percentSpent = 0, alerts = [], transactionCount = 0, transactions = [] } = budget;

  const handleDelete = async () => {
    if (!window.confirm(`Delete budget "${name}"?`)) return;
    setDeleting(true);
    try { await onDelete(budget._id); } finally { setDeleting(false); }
  };

  const fmtPeriod = () => {
    if (period === 'custom') {
      const s = budget.periodStart ? new Date(budget.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const e = budget.periodEnd   ? new Date(budget.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      return `${s} – ${e}`;
    }
    return period.charAt(0).toUpperCase() + period.slice(1);
  };

  const isOver = percentSpent >= 100;

  return (
    <div style={{ ...styles.budgetCard, borderLeft: `4px solid ${pctColor(percentSpent)}` }}>
      {/* Top row */}
      <div style={styles.budgetCardTop}>
        <div style={styles.budgetCardLeft}>
          <span style={styles.budgetName}>{name}</span>
          <span style={styles.budgetMeta}>{fmtPeriod()} · {categories.join(', ')}</span>
        </div>
        <div style={styles.budgetCardRight}>
          <button onClick={() => onEdit(budget)} style={styles.iconBtn} title="Edit">
            <Edit2 size={15} color="#6b7280" />
          </button>
          <button onClick={handleDelete} disabled={deleting} style={styles.iconBtn} title="Delete">
            {deleting ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={15} color="#ef4444" />}
          </button>
        </div>
      </div>

      {/* Amounts */}
      <div style={styles.amountRow}>
        <div>
          <span style={{ ...styles.spentAmount, color: pctColor(percentSpent) }}>
            {formatAmount(spent, baseCurrency)}
          </span>
          <span style={styles.totalAmount}> / {formatAmount(amount, baseCurrency)}</span>
        </div>
        <span style={{ ...styles.pctBadge, backgroundColor: pctColor(percentSpent) + '20', color: pctColor(percentSpent) }}>
          {isOver ? '⚠ Over budget' : `${percentSpent}% used`}
        </span>
      </div>

      {/* Progress bar */}
      <ProgressBar pct={percentSpent} />

      {/* Remaining */}
      <div style={styles.remainingRow}>
        <span style={styles.metaText}>
          {isOver
            ? `${formatAmount(Math.abs(remaining), baseCurrency)} over budget`
            : `${formatAmount(remaining, baseCurrency)} remaining`}
        </span>
        <span style={styles.metaText}>{transactionCount} transaction{transactionCount !== 1 ? 's' : ''}</span>
      </div>

      {/* Expandable: transactions + alerts */}
      {(transactionCount > 0 || alerts.length > 0) && (
        <>
          <button onClick={() => setExpanded(e => !e)} style={styles.expandBtn}>
            <span>{transactionCount} transaction{transactionCount !== 1 ? 's' : ''}</span>
            {alerts.length > 0 && (
              <>
                <span style={{ color: t.borderLight }}>·</span>
                <Bell size={13} color="#6b7280" />
                <span>{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</span>
              </>
            )}
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {expanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {transactions.length > 0 && (
                <div style={styles.txList}>
                  {[...transactions]
                    .sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate))
                    .map((tx, i, arr) => {
                      const name = tx.receiver || tx.merchant || tx.category;
                      const amt = tx.currency && tx.currency.toUpperCase() !== baseCurrency.toUpperCase() && tx.convertedAmount != null
                        ? tx.convertedAmount : tx.amount;
                      return (
                        <div key={tx._id} style={{ ...styles.txItem, ...(i === arr.length - 1 ? { borderBottom: 'none' } : {}) }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: 1, minWidth: 0 }}>
                            <span style={styles.txName}>{name}</span>
                            <span style={styles.txMeta}>
                              {tx.category} · {new Date(tx.transactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <span style={styles.txAmount}>-{formatAmount(amt, baseCurrency)}</span>
                        </div>
                      );
                    })}
                </div>
              )}

              {alerts.length > 0 && (
                <div style={styles.alertsList}>
                  {[...alerts].sort((a, b) => a.percentage - b.percentage).map((a, i) => (
                    <div key={i} style={styles.alertItem}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, backgroundColor: a.triggered ? '#10b981' : t.textMuted }} />
                      <span style={styles.alertText}>
                        At {a.percentage}% — {a.triggered
                          ? `sent ${a.triggeredAt ? new Date(a.triggeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}`
                          : 'pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

function BudgetView({ baseCurrency = 'INR' }) {
  const t = useTokens();
  const styles = makeStyles(t);
  const { track } = useAnalytics();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // budget being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetAPI.getAll();
      setBudgets(res.data.budgets || []);
    } catch (err) {
      console.error('Failed to load budgets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    const res = await budgetAPI.create(data);
    track('budget_created', { period: data.period, categories: data.categories.length, alerts: data.alerts.length });
    setBudgets(prev => [res.data.budget, ...prev]);
    setShowForm(false);
  };

  const handleUpdate = async (data) => {
    const res = await budgetAPI.update(editTarget._id, data);
    track('budget_updated', { period: data.period });
    setBudgets(prev => prev.map(b => b._id === editTarget._id ? res.data.budget : b));
    setEditTarget(null);
  };

  const handleDelete = async (id) => {
    await budgetAPI.remove(id);
    track('budget_deleted');
    setBudgets(prev => prev.filter(b => b._id !== id));
  };

  const openEdit = (budget) => {
    setEditTarget(budget);
    setShowForm(false);
  };

  const closeForm = () => { setShowForm(false); setEditTarget(null); };

  const activeBudgets   = budgets.filter(b => b.active);
  const inactiveBudgets = budgets.filter(b => !b.active);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Budgets</h1>
          <p style={styles.subtitle}>Set limits and get email alerts when you're close</p>
        </div>
        <button
          data-tour="new-budget-btn"
          onClick={() => { setShowForm(true); setEditTarget(null); }}
          style={styles.newBtn}
          disabled={showForm || editTarget !== null}
        >
          <Plus size={16} /> New Budget
        </button>
      </div>

      {/* Data source note */}
      <div style={{ padding: '12px 14px', backgroundColor: t.chipBg, border: `1px solid ${t.border}`, borderRadius: '12px' }}>
        <span style={{ fontSize: '13px', color: t.textSecondary, lineHeight: '1.5' }}>
          Budgets track spending automatically when transactions sync via email forwarding. If you don't have one set up, upload bank statements regularly to keep your budgets accurate.
        </span>
      </div>

      {/* SMTP notice */}
      {/* <div style={styles.smtpNotice}>
        <AlertTriangle size={14} color="#d97706" />
        <span>
          Email alerts require SMTP configuration in your server's <code style={styles.code}>.env</code> file
          (<code style={styles.code}>SMTP_HOST</code>, <code style={styles.code}>SMTP_USER</code>, <code style={styles.code}>SMTP_PASS</code>).
          Alerts are checked hourly.
        </span>
      </div> */}

      {/* Create form */}
      {showForm && (
        <BudgetForm
          onSave={handleCreate}
          onCancel={closeForm}
          baseCurrency={baseCurrency}
        />
      )}

      {/* Edit form */}
      {editTarget && (
        <BudgetForm
          initial={{
            name:       editTarget.name,
            categories: editTarget.categories,
            amount:     String(editTarget.amount),
            period:     editTarget.period,
            startDate:  editTarget.startDate ? editTarget.startDate.slice(0, 10) : '',
            endDate:    editTarget.endDate   ? editTarget.endDate.slice(0, 10)   : '',
            alerts:     editTarget.alerts.map(a => ({ percentage: a.percentage })),
          }}
          onSave={handleUpdate}
          onCancel={closeForm}
          baseCurrency={baseCurrency}
        />
      )}

      {/* Loading */}
      {loading ? (
        <div style={styles.loader}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading budgets…</p>
        </div>
      ) : budgets.length === 0 && !showForm ? (
        <div style={styles.empty}>
          <Bell size={48} color="#d1d5db" />
          <p style={styles.emptyTitle}>No budgets yet</p>
          <p style={styles.emptyText}>Create a budget to track your spending and get email alerts.</p>
        </div>
      ) : (
        <>
          {activeBudgets.length > 0 && (
            <div data-tour="budget-list" style={styles.section}>
              <h2 style={styles.sectionTitle}>Active Budgets</h2>
              <div style={styles.cardList}>
                {activeBudgets.map(b => (
                  <BudgetCard
                    key={b._id}
                    budget={b}
                    baseCurrency={baseCurrency}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {inactiveBudgets.length > 0 && (
            <div style={styles.section}>
              <h2 style={{ ...styles.sectionTitle, color: t.textMuted }}>Paused Budgets</h2>
              <div style={styles.cardList}>
                {inactiveBudgets.map(b => (
                  <BudgetCard
                    key={b._id}
                    budget={b}
                    baseCurrency={baseCurrency}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

function makeStyles(t) { return {
  container: { padding: 0, display: 'flex', flexDirection: 'column', gap: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' },
  title: { margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700', color: t.text },
  subtitle: { margin: 0, fontSize: '13px', color: t.textMuted },
  newBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 16px', border: 'none', borderRadius: '8px',
    backgroundColor: '#2563eb', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  loader: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: t.textSecondary, gap: '12px' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '60px 20px', backgroundColor: t.surface, borderRadius: '12px', boxShadow: t.cardShadow, gap: '8px', textAlign: 'center',
  },
  emptyTitle: { fontSize: '16px', fontWeight: '600', color: t.text, margin: '8px 0 4px' },
  emptyText:  { fontSize: '13px', color: t.textMuted, margin: '0 0 16px' },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  sectionTitle: { margin: 0, fontSize: '14px', fontWeight: '600', color: t.text, textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  budgetCard: { backgroundColor: t.surface, borderRadius: '12px', boxShadow: t.cardShadow, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  budgetCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  budgetCardLeft: { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 },
  budgetCardRight: { display: 'flex', gap: '6px', flexShrink: 0 },
  budgetName: { fontSize: '15px', fontWeight: '600', color: t.text },
  budgetMeta: { fontSize: '12px', color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  amountRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  spentAmount: { fontSize: '20px', fontWeight: '700', fontFamily: 'monospace' },
  totalAmount: { fontSize: '14px', color: t.textMuted, fontFamily: 'monospace' },
  pctBadge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  remainingRow: { display: 'flex', justifyContent: 'space-between' },
  metaText: { fontSize: '12px', color: t.textMuted },
  expandBtn: { display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: t.textSecondary, padding: 0 },
  alertsList: { display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '4px' },
  alertItem:  { display: 'flex', alignItems: 'center', gap: '8px' },
  alertText: { fontSize: '12px', color: t.textSecondary },
  txList: { display: 'flex', flexDirection: 'column', gap: '0px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${t.border}` },
  txItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', backgroundColor: t.surface, borderBottom: `1px solid ${t.borderLight}` },
  txName: { fontSize: '13px', fontWeight: '500', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  txMeta: { fontSize: '11px', color: t.textMuted },
  txAmount: { fontSize: '13px', fontWeight: '600', color: t.text, flexShrink: 0, fontFamily: 'monospace' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' },
  formCard: { backgroundColor: t.surface, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' },
  formHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  formTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: t.text },
  errorBanner: { padding: '10px 14px', backgroundColor: t.dangerBg, borderRadius: '8px', color: t.danger, fontSize: '13px', border: `1px solid ${t.dangerBorder}` },
  label: { fontSize: '13px', fontWeight: '600', color: t.text, marginBottom: '-8px' },
  input: { padding: '9px 12px', border: `1px solid ${t.inputBorder}`, borderRadius: '8px', fontSize: '14px', color: t.text, outline: 'none', marginBottom: '2px', width: '100%', boxSizing: 'border-box', backgroundColor: t.inputBg },
  select: { padding: '9px 12px', border: `1px solid ${t.inputBorder}`, borderRadius: '8px', fontSize: '14px', color: t.text, outline: 'none', backgroundColor: t.inputBg, width: '100%', cursor: 'pointer' },
  dateRow: { display: 'flex', gap: '12px' },
  categoryGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  catChip: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '20px', border: `1px solid ${t.border}`, backgroundColor: t.chipBg, color: t.textSecondary, fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  catChipSelected: { backgroundColor: '#eff6ff', borderColor: '#93c5fd', color: '#2563eb' },
  alertsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  addAlertBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', border: `1px solid ${t.border}`, borderRadius: '6px', backgroundColor: t.chipBg, color: t.text, fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  alertHint: { margin: '-8px 0 0', fontSize: '12px', color: t.textSecondary },
  alertRow:  { display: 'flex', alignItems: 'center', gap: '8px' },
  alertPctLabel: { fontSize: '14px', color: t.textSecondary, flexShrink: 0 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' },
  cancelBtn: { padding: '9px 18px', border: `1px solid ${t.border}`, borderRadius: '8px', backgroundColor: t.surface, color: t.text, fontSize: '14px', cursor: 'pointer' },
  saveBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', border: 'none', borderRadius: '8px', backgroundColor: '#2563eb', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
}; }

export default BudgetView;
